import { observable, decorate, computed } from 'mobx';
import _ from 'lodash';

import { authUserState } from './common';
import { checkRights } from '../functions';
import { RightsAccess, NotificationsAccess, DashboardsAccess } from '../shared/AccessModels';

export class RoleState {
	constructor() {
		this.Roles = [];
		this.Rights = [];
		this.Departments = [];
		this.Notifications = [];
		this.IsSendRequest = false;

		this.Role = undefined;
		this.Department = undefined;
	}

	get CanManage () { return authUserState.User ? checkRights("ManageRoles") : false; }

	/** Формирование столбцов для таблицы должностей с учетом права управления */
	get columns() { return _.extend({ Name: {}, Department: {} }, this.CanManage ? { Edit: {}, Remove: {} } : {}); }

	/** ----- РАБОТА СО СПИСКОМ ДОЛЖНОСТЕЙ ----- */
	/**
	 * Разбор данных от сервера со списком должностей
	 * @param {object} data
	 */
	setRoles(data) {
		this.Roles = !data.Success ? [] : _.map(data.Roles, (v) => _.extend(v, { CanRemove : !v.IsDefault }));
		this.IsSendRequest = true;
	}

	/** ----- РАБОТА СО СТРАНИЦЕЙ ДОЛЖНОСТИ ----- */
	/** Выбор активного отдела */
	setDepartment(department) { this.Department = _.isUndefined(department) ? undefined : new Department(department); }

	/**
	 * Разбор данных от сервера с должностью
	 * @param {object} role
	 */
	setRole (role) {
		this.Role = _.isUndefined(role) ? undefined : new Role(role, {
			Rights : this.Rights,
			Notifications : this.Notifications,
			Dashboards : this.Dashboards
		});
	}
}
decorate(RoleState, {
	Notifications: observable,	// Список доступных уведомлений
	Roles : observable,			// Список доступных должностей
	Rights : observable,		// Список доступных прав
	Dashboards : observable,	// Список доступных рабочих столов
	IsSendRequest: observable,	// Флаг отправки запроса

	Departments: observable,	// Список доступных отделов
	Department: observable,		// Модель редактироваемого отдела

	Role: observable,			// Модель редактируемой должности
	CanManage: computed,		// Флаг права на управление должностями
	columns: computed			// Столбцы таблицы должностей
});

/** ----- КЛАСС ОТДЕЛА ----- */
export class Department {
	constructor(item) {
		this.Id = item && item.Id ? item.Id : -1;
		this.Name = item && item.Name ? item.Name : "";

		this.ErrorCode = "";
		this.SuccessSave = false;
		this.ButtonLoading = false;
	}

	/**
	 * Сбор данных с формы отдела
	 * @param {string} id название параметра
	 * @param {string} value значение параметра
	 */
	collect = (id, value) => { this[id] = value; }

	/**
	 * Сбор данных отдела для отправки на сервер
	 * @returns {{ Id: number, Name: string }} данные отдела в формате API DepartmentSave
	 */
	getData () {
		return {
			Id : this.Id,
			Name : this.Name
		};
	}
}
decorate(Department, {
	Id: observable,
	Name: observable,
	ButtonLoading: observable,
	ErrorCode: observable,
	SuccessSave: observable
});

/** ----- КЛАСС ДОЛЖНОСТИ ----- */
export class Role {
	constructor(item, lists = {}) {
		this.Id = item && item.Id ? item.Id : -1;
		this.Active = item && !_.isUndefined(item.Active) ? item.Active : true;
		this.Name = item && item.Name ? item.Name : "";
		this.DepartmentId = item && item.DepartmentId ? item.DepartmentId : -1;

		this.Rights = new RightsAccess(item && item.Rights, lists.Rights);
		this.Notifications = new NotificationsAccess(item && item.Notifications, lists.Notifications);
		this.Dashboards = new DashboardsAccess(item && item.Dashboards, lists.Dashboards);

		this.IsCourier = item && !_.isUndefined(item.IsCourier) ? item.IsCourier : false;
		this.IsDefault = item && !_.isUndefined(item.IsDefault) ? item.IsDefault : false;

		this.ErrorCode = "";
		this.SuccessSave = false;
		this.ButtonLoading = false;
	}

	get PageTitle () { return this.Id === -1 ? "Новая должность" : "Редактирование должности"; }

	/**
	 * Проверка корректности заполнения должности
	 * 1. Должно быть заполнено название должности
	 * 2. Должен быть выбран отдел
	 * 3. Должен быть выбран хотя бы один рабочий стол в разделе Menu
	 * @returns {boolean} флаг корректности данных
	 */
	validate () {
		this.ErrorCode = "";
		this.SuccessSave = false;

		if (this.Name === "") {
			this.ErrorCode = "WRONG_PARAMS_NAME";
			return false;
		}

		if (this.DepartmentId === "" || this.DepartmentId < 1) {
			this.ErrorCode = "WRONG_PARAMS_DEPARTMENT";
			return false;
		}

		if (_.isEmpty(this.Dashboards.Checked.Menu)) {
			this.ErrorCode = "WRONG_PARAMS_MENU";
			return false;
		}

		return true;
	}

	/**
	 * Сбор данных с формы редактирования должности
	 * @param {string} id название параметра
	 * @param {string} value значение параметра
	 */
	collect = (id, value) => { this[id] = value; }

	/**
	 * Сбор данных должности для отправки на сервер
	 * @returns {{
	 *   Id: number,
	 *   Active: boolean,
	 *   Name: string,
	 *   DepartmentId: number,
	 *   Notifications: string,
	 *   Rights?: string,
	 *   "Dashboards.Menu"?: string,
	 *   "Dashboards.Sidebar"?: string,
	 *   "Dashboards.Widget"?: string
	 * }} данные должности в формате API RoleSave
	 */
	getData () {
		var data = {
			Id : this.Id,
			Active : this.Active,
			Name : this.Name,
			DepartmentId : this.DepartmentId,
			Notifications : this.Notifications.getData().join(",")
		};

		if (!_.isEmpty(this.Rights.getData())) data.Rights = this.Rights.getData().join(",");

		_.each(this.Dashboards.getData(), (list, type) => {
			data["Dashboards." + type] = list.join(",");
		});

		return data;
	}
}
decorate(Role, {
	Id: observable,
	Name: observable,
	DepartmentId: observable,
	Rights: observable,
	Dashboards: observable,
	Notifications: observable,
	ButtonLoading: observable,
	ErrorCode: observable,
	SuccessSave: observable
});
